import React, { useEffect, useRef, useState, useMemo } from 'react';
import ClosedSidebar from '../../components/closedSidebar';
import OpenedSidebar from '../../components/openedSidebar';
import { DragDropContext } from '@hello-pangea/dnd';
import { useDispatch, useSelector } from 'react-redux';
import { tentActions } from '../../redux-utils/actions/tent-actions';
import { useAuth } from '../../custom-hooks/useAuth';
import { useParams } from 'react-router';
import { dashboardActions } from '../../redux-utils/actions/dashboard-actions';
import TentInfo from '../../components/TentInfo';
import AvailableParticipants from '../../components/AvailableParticipants';
import Loader from '../../components/Loader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function ParticipantAssignment() {
    const [isOpened, setIsOpened] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { getLogin } = useAuth();
    const {
        singleTentAction,
        detachParticipantAction,
        assignParticipantAction,
    } = tentActions;
    const { allParticipantAction } = dashboardActions;
    const [selectedTent, setSelectedTent] = useState({});
    const selectedTentRef = useRef({});
    const loginStorage = JSON.parse(getLogin());
    const { allParticipantData, status } = useSelector(
        (state) => state.dashboardState
    );

    const { singleTentData } = useSelector((state) => state.tentState);
    const [tentParticipants, setTentParticipants] = useState([]);
    const [availableParticipants, setAvailableParticipants] = useState([]);
    const toggleSidebar = () => setIsOpened(!isOpened);

    const filteredParticipants = useMemo(() => {
        return availableParticipants.filter((p) =>
            `${p.first_name} ${p.last_name}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        );
    }, [availableParticipants, searchTerm]);

    const onDragEnd = ({ source, destination }) => {
        if (!source || !destination) return;

        const sourceId = source.droppableId;
        const destinationId = destination.droppableId;

        // Moving from Tent to AvailableParticipants
        if (sourceId.startsWith('tent-') && destinationId === 'participants') {
            const moved = tentParticipants[source.index];

            // Prevent duplicate addition
            if (!availableParticipants.some((p) => p.id === moved.id)) {
                setTentParticipants((prev) =>
                    prev.filter((_, i) => i !== source.index)
                );
                setAvailableParticipants((prev) => [...prev, moved]);

                const params = {
                    tent_id: sourceId.replace('tent-', ''),
                    participant_id: moved.id,
                    token: loginStorage && loginStorage.loginData.data.token,
                };

                dispatch(detachParticipantAction(params))
                    .then(() => {
                        toast('Participant detached from tent successfully!');
                    })
                    .catch(() => {
                        toast('Failed to detach participant from participant.');
                    });
            }
        }

        // Moving from AvailableParticipants to Tent
        if (sourceId === 'participants' && destinationId.startsWith('tent-')) {
            const moved = availableParticipants[source.index];
            const tentId = destinationId.replace('tent-', '');

            // Prevent duplicate addition
            if (!tentParticipants.some((p) => p.id === moved.id)) {
                setAvailableParticipants((prev) =>
                    prev.filter((_, i) => i !== source.index)
                );
                setTentParticipants((prev) => [...prev, moved]);

                const params = {
                    tent_id: tentId,
                    participant_id: moved.id,
                    token: loginStorage && loginStorage.loginData.data.token,
                };

                dispatch(assignParticipantAction(params))
                    .then(() => {
                        toast('Participant assigned to tent successfully!');
                    })
                    .catch(() => {
                        toast('Failed to assign participant to tent');
                    });
            }
        }
    };

    useEffect(() => {
        if (id && loginStorage?.loginData?.data?.token) {
            const params = {
                id,
                token: loginStorage.loginData.data.token,
            };
            dispatch(singleTentAction(params));
        }
    }, [id, dispatch]);

    useEffect(() => {
        if (
            singleTentData &&
            Object.keys(singleTentData).length > 0 &&
            JSON.stringify(singleTentData) !==
                JSON.stringify(selectedTentRef.current)
        ) {
            selectedTentRef.current = singleTentData;
            setSelectedTent(singleTentData);
            setTentParticipants(singleTentData.participants || []);
        }
    }, [singleTentData, selectedTent]);

    useEffect(() => {
        if (allParticipantData?.length) {
            setAvailableParticipants(allParticipantData);
        }
    }, [allParticipantData]);

    useEffect(() => {
        const allParam = {
            token: loginStorage && loginStorage.loginData.data.token,
        };
        dispatch(allParticipantAction(allParam));
    }, []);

    useEffect(() => {
        if (allParticipantData?.length) {
            const uniqueParticipants = allParticipantData.filter(
                (p) => !tentParticipants.some((tp) => tp.id === p.id)
            );
            setAvailableParticipants(uniqueParticipants);
        }
    }, [allParticipantData, tentParticipants]);

    return (
        <main className="font-sans flex min-h-screen bg-gray-100">
            {isOpened ? (
                <OpenedSidebar toggleSidebar={toggleSidebar} />
            ) : (
                <ClosedSidebar toggleSidebar={toggleSidebar} />
            )}

            {status === 'all_participant_fetch_start' && <Loader />}

            <section className="w-full px-8 py-6 overflow-auto">
                <h1 className="text-tertiary text-xl font-semibold mb-8">
                    {t('tent-management-page.Assign Participants To Tent')}
                </h1>
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="flex gap-6 lg:flex-row flex-col h-full">
                        <TentInfo
                            tent={selectedTent}
                            participants={tentParticipants}
                        />
                        <AvailableParticipants
                            participants={filteredParticipants}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                        />
                    </div>
                </DragDropContext>
            </section>
        </main>
    );
}

export default ParticipantAssignment;
